import { isNil } from 'lodash';
import { IntMaxValue, MaxTicks, TimeSpanMaxValue } from 'mbs-ui-kit/utils/constants';
import { AzureAccessTierType, StorageClass, StorageType, TypesForSynthetic } from 'mbs-ui-shared@models';
import { PlanTypes, PlanTypesDisplay, PlanVMNewTypesDisplay } from 'mbs-ui-shared@models/PlanTypes.enum';
import { RecurType } from 'mbs-ui-shared@models/RecurType.enum';
import { ImmutabilityMode } from 'mbs-ui-shared@models/StorageAccount';
import { Destination, SAWithDestinations } from 'mbs-ui-shared@models/StorageAccountsWithDestinations';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { GetDateFromString, TimeSpanToTimeGroup, mediumDateWithTimeMoment } from 'mbs-ui-shared@utils/date';
import { ScheduleDataHelper } from 'mbs-ui-shared@utils/schedule-data-helper';
import moment from 'moment';
import { AdvancedOptionsFlags } from 'wizards/models/advanced-options-models';
import { BackupAction } from 'wizards/models/base/base-models/plan-backup-actions-model';
import { Storages } from 'wizards/models/base/base-models/plan-storages-model';
import { BaseRetentionPolicy } from 'wizards/models/retention-policy-models';
import { ScheduleFormSettings, ScheduleSettings } from 'wizards/models/schedule-advanced-models';
import { VolumeInfoCommunication } from '../models/base/base-models/plan-volume-info-model';

const MAX_SAFE_BIGINT = BigInt(Number.MAX_SAFE_INTEGER);

export type AdvancedStorageSettings = { azureType?: AzureAccessTierType; isStorageBadForFFI?: boolean; storageClass?: StorageClass };

export type DecodedGuid =
  | { type: 'hex'; value: number }
  | { type: 'int32'; value: number }
  | { type: 'int64'; value: number }
  | { type: 'guid'; value?: number };

export class SupportMethodsForStepsFromPlan {
  private static planTypesWithCompName = [PlanTypes.Plan, PlanTypes.DataBasePlan];
  public static generatePlanName(compName: string, planType: PlanTypes, offset: number): string {
    const compNamePart = compName && this.planTypesWithCompName.includes(planType) ? `of ${compName} ` : '';
    const provider = moment.utc().utcOffset(offset);
    const datePart = `on ${provider.format(mediumDateWithTimeMoment)}`;
    return `${PlanTypesDisplay[planType] || PlanVMNewTypesDisplay[planType]} ${compNamePart}${datePart}`;
  }

  public static needUpdateExcludeRules(volume: VolumeInfoCommunication): boolean {
    return volume?.BackupOptions && !!volume.BackupOptions?.ExcludeRules?.length;
  }

  public static getExcludeRuleString(mountPoints: string[], WindowsVolumeIdentity: string, folder: string, needVolume = false): string {
    let excludeRule = '';
    if (needVolume && WindowsVolumeIdentity) excludeRule += 'Volume{' + WindowsVolumeIdentity + '}';
    else if (mountPoints.length) excludeRule += mountPoints[0].replace(/\\$/, '');
    else excludeRule += '\\\\?\\Volume{' + WindowsVolumeIdentity ? WindowsVolumeIdentity : '' + '}';
    excludeRule += folder;
    return excludeRule;
  }

  public static getExcludeRules(diskInfo: any[]): any[] {
    if (diskInfo.length) {
      const excludes: string[] = [];
      diskInfo.forEach((disk) => {
        if (disk.Volumes) {
          disk.Volumes.forEach((volume) => {
            if (this.needUpdateExcludeRules(volume)) {
              volume.BackupOptions.ExcludeRules.forEach((excl) => {
                const excludeRule = this.getExcludeRuleString(volume.MountPoints, volume.WindowsVolumeIdentity, excl.Folder);
                excludes.push(excludeRule);
              });
            }
          });
        }
      });
      return excludes;
    }
    return [];
  }

  public static getScheduleType(isPredefined, isSchedule, isOtherSchedule, RecurType: RecurType, isUnix = false): string {
    let scheduleType = 'noschedule';
    if (isSchedule) scheduleType = this.setScheduleTypeIfSchedule(isPredefined, RecurType, isUnix);
    else if (isOtherSchedule) scheduleType = !isUnix && isPredefined ? 'predefined' : 'recurring';
    return scheduleType;
  }

  public static setScheduleTypeIfSchedule(isPredefined: boolean, RecType: RecurType, isUnix = false): string {
    if (!isUnix) {
      return RecType === RecurType.Instantly
        ? 'instantly'
        : isPredefined || RecType === RecurType.Periodically
          ? 'predefined'
          : RecType === RecurType.Once
            ? 'once'
            : 'recurring';
    } else return RecType === RecurType.Once ? 'once' : 'recurring';
  }

  public static setSpecificDateGroup(
    isOnce: boolean,
    OnceDate: string = null,
    isUtc = false
  ): { date: Date | string; time: Date | string } {
    if (isOnce) {
      if (isUtc) {
        const point = moment.utc(OnceDate);
        return { date: point.toDate(), time: point.format('HH:mm:ss') };
      }
      const newDate = moment(OnceDate).toDate();
      return { date: newDate, time: moment(OnceDate).format('HH:mm:ss') };
    }
    const date = moment(new Date()).add(1, 'hours').set('minute', 0).toDate();
    return { date: date, time: moment().format('HH:mm:ss') };
  }

  public static getScheduleDiffOrTLog(schedule: ScheduleSettings): ScheduleFormSettings {
    const isSchedule = !(schedule === null || schedule === undefined);
    const scheduleFormSettings = new ScheduleFormSettings();
    scheduleFormSettings.Enabled = isSchedule ? schedule.Enabled : null;
    scheduleFormSettings.FormSchedule = isSchedule ? ScheduleDataHelper.generateNewRecurringSchedule(schedule) : null;
    return scheduleFormSettings;
  }

  public static getStoragesFromStorageAccounts(Accounts: SAWithDestinations[]): Storages {
    const cloudStorages: StorageConnection[] = [];
    const localStorages: StorageConnection[] = [];
    Accounts.forEach((storageAccount: SAWithDestinations) => {
      if (storageAccount.storageType === StorageType.AzureVM) return;
      const storageTypeLocal = storageAccount.storageType === StorageType.LocalFS;
      storageAccount.buckets.forEach((bucket: Destination) => {
        const newStorage = {
          DisplayName: `${bucket.destinationName} (${bucket.bucketName})`,
          ID: bucket.id,
          StorageType: storageAccount.storageType,
          ImmutabilityMode: bucket.immutabilityMode,
          ImmutabilityPeriodDays: bucket.immutabilityPeriodDays,
          S3CompatibleSynthetic: storageAccount.S3CompatibleSynthetic,
          StorageAccountType: null,
          MinimumStorageDuration: storageAccount.MinimumStorageDuration
        };
        storageTypeLocal ? localStorages.push(newStorage) : cloudStorages.push(newStorage);
      });
    });
    return { CloudStorages: cloudStorages, LocalStorages: localStorages };
  }

  public static getFilteredStoragesByTypes(storages: Storages, types: StorageType[]): void {
    storages.CloudStorages = storages.CloudStorages.filter((s) => !types.some((t) => t === s.StorageType));
    storages.LocalStorages = storages.LocalStorages.filter((s) => !types.some((t) => t === s.StorageType));
  }

  public static getHybridRetentionPolicy(newPlan, isUnix = false, isIbb = false): BaseRetentionPolicy {
    const helpData = this.getRetentionPolicyHelpData(newPlan);
    const data: BaseRetentionPolicy = {
      RetentionUseDefaultSettings: !newPlan.RetentionUseDefaultSettings,
      keepNumberOfVersions: !helpData.equalMax,
      RetentionNumberOfVersions:
        helpData.equalMax || newPlan.RetentionNumberOfVersions === null || newPlan.RetentionNumberOfVersions === undefined
          ? 3
          : newPlan.RetentionNumberOfVersions,
      deleteVersionsOlderThan: helpData.isDeleteOlderThan,
      deleteVersionsOlderThanCount: helpData.timeGroup.value,
      deleteVersionsOlderThanPeriod: helpData.timeGroup.timePeriod
    };
    if (!isIbb) {
      data.alwaysKeepLastVersion =
        (helpData.isDeleteOlderThan && !newPlan.RetentionDeleteLastVersion) || !newPlan.RetentionUseDefaultSettings;
      data.fromTimeSelected = newPlan.UseBackupDate ? 1 : 0;
      if (!isUnix) {
        const RetDelay = newPlan.RetentionDelay && newPlan.RetentionDelay !== '00:00:00';
        const serialRetDelay = newPlan.SerializationSupportRetentionDelay && newPlan.SerializationSupportRetentionDelay !== '00:00:00';
        data.delayPurgeForEnabled = RetDelay || serialRetDelay;
        data.delayPurgeFor = helpData.delayGroup.value;
        data.delayPurgeForSelect = helpData.delayGroup.timePeriod;
      }
    }
    return data;
  }

  public static getRetentionPolicyHelpData(newPlan: any): any {
    const RetTime = newPlan.RetentionTime && newPlan.RetentionTime !== '00:00:00' && newPlan.RetentionTime !== TimeSpanMaxValue;
    const serialRetTime = newPlan.SerializationSupportRetentionTime && newPlan.SerializationSupportRetentionTime !== '00:00:00';
    const retentionTime = RetTime ? newPlan.RetentionTime : newPlan.SerializationSupportRetentionTime;
    const equalMaxTime = retentionTime === TimeSpanMaxValue || !!(retentionTime && retentionTime.Ticks && retentionTime.Ticks === MaxTicks);
    const timeGroup = !equalMaxTime && (RetTime || serialRetTime) ? TimeSpanToTimeGroup(retentionTime) : { value: 1, timePeriod: 2 };

    const RetDelay = newPlan.RetentionDelay && newPlan.RetentionDelay !== '00:00:00' && newPlan.RetentionDelay !== TimeSpanMaxValue;
    const serialRetDelay = newPlan.SerializationSupportRetentionDelay && newPlan.SerializationSupportRetentionDelay !== '00:00:00';
    const retentionDelay = RetDelay ? newPlan.RetentionDelay : newPlan.SerializationSupportRetentionDelay;
    const delayGroup = RetDelay || serialRetDelay ? TimeSpanToTimeGroup(retentionDelay) : { value: 3, timePeriod: 0 };
    const isDeleteOlderThan = !newPlan.RetentionUseDefaultSettings && !equalMaxTime;
    const equalMax =
      newPlan.RetentionNumberOfVersions === null ||
      newPlan.RetentionNumberOfVersions === undefined ||
      newPlan.RetentionNumberOfVersions === IntMaxValue;
    return { equalMax, equalMaxTime, timeGroup, isDeleteOlderThan, delayGroup };
  }

  public static getOnceDateFromSchedule(schedule): Date {
    return schedule && (schedule.Enabled || schedule.RecurType === RecurType.Once) ? GetDateFromString(schedule.OnceDate) : new Date();
  }

  public static updateCanBackupEmptyFolders(event: StorageType, planType: PlanTypes): boolean {
    if (planType === PlanTypes.Plan) {
      return event !== StorageType.Azure && event !== StorageType.BackblazeB2 && event !== StorageType.Minio;
    } else return true;
  }

  /**
   * The following parameters are set on the Agent side
   *         private const long WasabiDays = 90; // WEB analog StorageType.Wasabi
   *         private const long WasabiUBDays = 30; // WEB analog StorageType.CblWasabi
   *         private const long AWSDays = 30; // WEB analog StorageClass.STANDARD_IA and StorageClass.ONEZONE_IA
   *         private const long AWSGlacierDays = 90; // WEB analog StorageClass.GLACIER
   *       * private const long AWSGlacierIRDays = 90; // WEB analog StorageClass.GLACIER_IR
   *         private const long AWSGlacierDeepArchiveDays = 180; // WEB analog StorageClass.DEEP_ARCHIVE
   *         For Azure: Cool - 30 days, Cold - 90 days, Archive - 180 days
   *
   * UB S3 is not included in the list of storages with a minimum retention period
   * StorageClass it is Storage, which was changed by Advanced Options step
   */
  private static minPeriodValues = {
    Storage: {
      [StorageType.Wasabi]: 90,
      [StorageType.CblWasabi]: 30
    },
    StorageClass: {
      [StorageClass.STANDARD_IA]: 30,
      [StorageClass.ONEZONE_IA]: 30,
      [StorageClass.GLACIER]: 90,
      [StorageClass.GLACIER_IR]: 90,
      [StorageClass.DEEP_ARCHIVE]: 180
    },
    AzureStorage: {
      [AzureAccessTierType.Cool]: 30,
      [AzureAccessTierType.Cold]: 90,
      [AzureAccessTierType.Archive]: 180
    }
  };

  public static getStorageMinPeriod(storage: StorageConnection, advancedStorage?: AdvancedStorageSettings): number {
    const storageMinValue = this.minPeriodValues.Storage[storage?.StorageType];

    if (storageMinValue) return storage?.MinimumStorageDuration || storageMinValue || 0;

    if (!isNil(advancedStorage?.azureType)) {
      return this.minPeriodValues.AzureStorage[advancedStorage?.azureType] || storage?.MinimumStorageDuration || 0;
    }

    return this.minPeriodValues.StorageClass[advancedStorage?.storageClass] || storage?.MinimumStorageDuration || 0;
  }

  public static getStorageAdvancedOptionsFlags(
    flags: AdvancedOptionsFlags,
    params: {
      agentVersion: string;
      cloudStorage: StorageConnection;
      isNBF: boolean;
      isNetBuild: boolean;
      isUnix: boolean;
      storage: StorageConnection;
      updateSynthetic: boolean;
    }
  ): AdvancedOptionsFlags {
    const { agentVersion, cloudStorage, isNBF, isNetBuild, isUnix, storage, updateSynthetic } = params;
    const storageType = storage?.StorageType;
    const isAmazonS3 = [StorageType.AmazonS3, StorageType.AmazonS3Eu].includes(storageType);
    const result: AdvancedOptionsFlags = {
      ...flags,
      AzureAccessTierType: storageType === StorageType.Azure,
      StorageClassSettingPossible: isAmazonS3,
      UseS3AccelerationPossible: isAmazonS3
    };

    if (!updateSynthetic) return result;

    const syntheticStorage = { StorageType: storageType, S3CompatibleSynthetic: cloudStorage?.S3CompatibleSynthetic };

    result.SyntheticFullSettingPossible = isNBF
      ? (!isUnix || isNetBuild) && TypesForSynthetic.isSupported(syntheticStorage, agentVersion)
      : ((!isUnix || isNetBuild) && cloudStorage?.S3CompatibleSynthetic) || TypesForSynthetic.CBF.isSupported({ StorageType: storageType });

    return result;
  }

  public static advancedOptionsNeedShow(flags: AdvancedOptionsFlags, planType: PlanTypes): boolean {
    return (
      !!flags &&
      Object.keys(flags).some((key) => (key !== 'SyntheticFullSettingPossible' ? flags[key] : flags[key] && planType !== PlanTypes.Plan))
    );
  }

  public static needShowImmutability(StorageConnections: StorageConnection[], id: string): boolean {
    return StorageConnections.some(
      (s) =>
        s.ID === id &&
        !(s.ImmutabilityMode === null || s.ImmutabilityMode === undefined) &&
        s.ImmutabilityMode !== ImmutabilityMode.Disabled
    );
  }

  public static getActionString(action: BackupAction): string {
    return action
      ? action.FullCommandLine
        ? action.FullCommandLine
        : !action.Arguments
          ? action.CommandLine
          : `${action.CommandLine}  ${action.Arguments}`
      : '';
  }

  public static decodeGuidToNumber(uuid: string): DecodedGuid {
    if (!uuid || typeof uuid !== 'string') return { type: 'guid', value: null };

    const parts = uuid.split('-');

    if (parts.length !== 5) return { type: 'guid', value: null };

    if (parts.slice(1).every((p) => /^0+$/.test(p)) && !/^0+$/.test(parts[0])) {
      return { type: 'hex', value: parseInt(parts[0], 16) };
    }

    const hex = uuid.replace(/-/g, '');

    if (hex.length !== 32) return { type: 'guid', value: null };

    const bytes = new Uint8Array(16);

    for (let i = 0; i < 32; i += 2) {
      bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
    }

    const int32 = (bytes[3] << 24) | (bytes[2] << 16) | (bytes[1] << 8) | bytes[0];

    if (int32 !== 0) return { type: 'int32', value: int32 >>> 0 };

    let int64 = 0n;

    for (let i = 7; i >= 0; i--) {
      int64 = (int64 << 8n) + BigInt(bytes[i]);
    }

    if (int64 !== 0n && int64 <= MAX_SAFE_BIGINT) return { type: 'int64', value: Number(int64) };

    return { type: 'guid', value: null };
  }
}
